import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import type { ApplicationStatus } from '@/types/api'

/**
 * The one way a status is shown everywhere: a dot, a short label, and a
 * tone. Running states pulse. Keep every status label in STATUS_META so
 * the same word is used on the dashboard, the tracker, the review page
 * and in notifications.
 */
export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand'

export const STATUS_META: Record<
  ApplicationStatus,
  { label: string; tone: Tone; live?: boolean; short: string }
> = {
  queued: { label: 'Queued', short: 'Queued', tone: 'info', live: true },
  in_flight: { label: 'Applying', short: 'Applying', tone: 'info', live: true },
  needs_you: { label: 'Needs user', short: 'Needs user', tone: 'warning' },
  submitted: { label: 'Submitted', short: 'Sent', tone: 'success' },
  failed: { label: 'Failed', short: 'Failed', tone: 'danger' },
  skipped: { label: 'Skipped', short: 'Skipped', tone: 'neutral' },
  draft: { label: 'Draft', short: 'Draft', tone: 'neutral' },
  job_expired: { label: 'Job expired', short: 'Expired', tone: 'neutral' },
}

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-ink/[0.06] text-ink-soft',
  success: 'bg-success/12 text-success',
  warning: 'bg-warning/14 text-warning',
  danger: 'bg-danger/12 text-danger',
  info: 'bg-info/12 text-info',
  brand: 'bg-brand-soft text-brand',
}

export function StatusPill({
  tone = 'neutral',
  live,
  children,
  className,
  size = 'md',
  title,
}: {
  tone?: Tone
  /** A pulsing dot: the state is changing on its own (queued, applying, waiting). */
  live?: boolean
  children: ReactNode
  className?: string
  size?: 'sm' | 'md'
  title?: string
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill font-medium',
        size === 'sm' ? 'h-5 px-2 text-[11px]' : 'h-6 px-2.5 text-[12px]',
        TONE_CLASS[tone],
        className,
      )}
    >
      <span className="relative flex h-1.5 w-1.5">
        {live && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />}
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" />
      </span>
      {children}
    </span>
  )
}

/** The pill for an application's status; "Ready to submit" wins when the details are all filled. */
export function ApplicationStatusPill({
  status,
  readyToSubmit,
  needsReview,
  size,
  className,
}: {
  status: ApplicationStatus
  readyToSubmit?: boolean
  /** The run stopped and is parked with its reason: "Needs review", not "Needs user". */
  needsReview?: boolean
  size?: 'sm' | 'md'
  className?: string
}) {
  if (readyToSubmit) {
    return (
      <StatusPill tone="brand" size={size} className={className}>
        Ready to submit
      </StatusPill>
    )
  }
  if (needsReview && status === 'needs_you') {
    return (
      <StatusPill tone="warning" size={size} className={className}>
        Needs review
      </StatusPill>
    )
  }
  const meta = STATUS_META[status] ?? { label: status, tone: 'neutral' as Tone }
  return (
    <StatusPill tone={meta.tone} live={meta.live} size={size} className={className}>
      {meta.label}
    </StatusPill>
  )
}
